import type { FormElementBase } from "~/api/generated/models/FormElementBase";
import { FormElementType } from "~/api/generated/models/FormElementType";
import { ValidationType } from "~/api/generated/models/ValidationType";
import { validationDict } from "~/NewFormBuilder/utils/validationTypeOptions";

/**
 * Counts words in text, handling both plain text and HTML content
 */
function countWords(text: string, isRichText: boolean = false): number {
  if (!text || text.trim() === "") {
    return 0;
  }

  let processedText = text;

  if (isRichText) {
    const noHtml = text.replaceAll(/<[^>]*>/g, "");
    processedText = noHtml.replaceAll(/&[a-zA-Z0-9#]+;/g, "");
  }

  const words = processedText.match(/\b\w+([-'\u2018\u2019]\w+)*\b/g);
  return words ? words.length : 0;
}

/**
 * Checks if an answer is valid for a given form component.
 * Empty answers are always considered valid (allows deletion).
 */
export function isAnswerValid(
  component: FormElementBase,
  answerValue: unknown,
): boolean {
  if (answerValue === undefined || answerValue === null || answerValue === "") {
    return true;
  }

  const stringValue = answerValue.toString();

  if (
    component.type === FormElementType.SIMPLE_TEXT ||
    component.type === FormElementType.RICH_TEXT
  ) {
    const maxWordCount = (component.properties as { maxWordCount?: number })
      .maxWordCount;
    if (maxWordCount) {
      const isRichText = component.type === FormElementType.RICH_TEXT;
      const currentWordCount = countWords(stringValue, isRichText);
      if (currentWordCount > maxWordCount) {
        return false;
      }
    }
  }

  if (component.type === FormElementType.SIMPLE_TEXT) {
    const validationType = (
      component.properties as { validationType?: ValidationType }
    ).validationType;
    if (validationType) {
      const validator = validationDict[validationType]?.validator;
      if (validator && !validator(stringValue)) {
        return false;
      }
    }
  }

  return true;
}
